import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const EFFECTIVE_DATE = 'May 9, 2026';

const TermsPage: React.FC = () => (
  <div className="legal-page">
    <div className="legal-header">
      <h1>Terms of Use</h1>
      <p className="legal-meta">Effective date: {EFFECTIVE_DATE}</p>
    </div>

    <div className="legal-toc">
      <h2>Contents</h2>
      <ol>
        <li><a href="#acceptance">Acceptance of Terms</a></li>
        <li><a href="#service">Service Description</a></li>
        <li><a href="#pricing">Pricing and Plan Changes</a></li>
        <li><a href="#accounts">User Accounts</a></li>
        <li><a href="#acceptable-use">Acceptable Use</a></li>
        <li><a href="#content">Your Content</a></li>
        <li><a href="#sharing">Document Sharing</a></li>
        <li><a href="#ip">Intellectual Property</a></li>
        <li><a href="#retention">Data Retention</a></li>
        <li><a href="#liability">Disclaimer and Limitation of Liability</a></li>
        <li><a href="#termination">Termination</a></li>
        <li><a href="#changes">Changes to These Terms</a></li>
        <li><a href="#contact-terms">Contact</a></li>
      </ol>
    </div>

    <div className="legal-section" id="acceptance">
      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using KeeDocs (the "Service"), you agree to be bound by these
        Terms of Use ("Terms"). If you do not agree, do not use the Service. These Terms apply to
        all visitors, users, and registered account holders.
      </p>
    </div>

    <div className="legal-section" id="service">
      <h2>2. Service Description</h2>
      <p>
        KeeDocs is a secure document-sharing platform that allows you to upload, store, share,
        and control access to documents and files. The Service includes:
      </p>
      <ul>
        <li>Authenticated document storage and management</li>
        <li>Guest (anonymous) uploads with time-limited sharing links</li>
        <li>Granular permission controls (view, download, print, watermark)</li>
        <li>Document analytics and access tracking</li>
      </ul>
    </div>

    <div className="legal-section" id="pricing">
      <h2>3. Pricing and Plan Changes</h2>
      <p>
        KeeDocs is currently offered free of charge. However, <strong>we reserve the right to
        introduce paid plans, modify existing plan limits, or transition the Service (in whole or
        in part) to a paid model at any time and without prior notice.</strong> Continued use of
        the Service after such a change constitutes your acceptance of the new pricing or plan
        structure.
      </p>
      <p>
        If a feature or storage tier you rely on becomes part of a paid plan, you will need to
        upgrade or cease using that feature. We are not obligated to grandfather any existing free
        usage.
      </p>
    </div>

    <div className="legal-section" id="accounts">
      <h2>4. User Accounts</h2>
      <p>
        To access the full Service you must register for an account. You agree to:
      </p>
      <ul>
        <li>Provide accurate and complete information during registration</li>
        <li>Keep your password secure and not share it with others</li>
        <li>Notify us immediately at <a href="mailto:support@keedocs.com">support@keedocs.com</a> if
          you suspect unauthorised access to your account</li>
        <li>Be responsible for all activity that occurs under your account</li>
      </ul>
      <p>
        You must be at least 18 years old, or the age of legal majority in your jurisdiction, to
        create an account. Accounts registered with false information may be suspended without notice.
      </p>
    </div>

    <div className="legal-section" id="acceptable-use">
      <h2>5. Acceptable Use</h2>
      <p>You may not use the Service to:</p>
      <ul>
        <li>Upload, share, or distribute content that is illegal, harmful, threatening, abusive,
          defamatory, or infringes any third-party rights</li>
        <li>Upload malware, viruses, or any code designed to interrupt or damage systems</li>
        <li>Circumvent, disable, or interfere with security features of the Service</li>
        <li>Use automated tools to access the Service in a manner that exceeds reasonable personal use</li>
        <li>Resell or sublicense access to the Service without our written consent</li>
        <li>Impersonate any person or entity, or misrepresent your affiliation with a person or entity</li>
      </ul>
      <p>
        We reserve the right to investigate and take appropriate legal action against anyone who, in
        our sole discretion, violates this policy.
      </p>
    </div>

    <div className="legal-section" id="content">
      <h2>6. Your Content</h2>
      <p>
        You retain ownership of all documents and files you upload to the Service ("Your Content").
        By uploading content, you grant KeeDocs a limited, non-exclusive, royalty-free licence to
        store, process, and transmit Your Content solely to provide the Service to you.
      </p>
      <p>
        You are solely responsible for ensuring that Your Content does not violate any applicable
        laws or third-party rights. We do not review content before it is uploaded.
      </p>
    </div>

    <div className="legal-section" id="sharing">
      <h2>7. Document Sharing</h2>
      <p>
        When you share a document with another user or generate a sharing link, you control the
        access permissions. You acknowledge that:
      </p>
      <ul>
        <li>Anyone with a valid sharing link can access the document according to the permissions
          you set</li>
        <li>Anonymous (guest) uploads are stored for a maximum of <strong>7 days</strong> and are
          then permanently deleted</li>
        <li>Authenticated user documents are stored while your account remains active</li>
        <li>You should not share confidential or sensitive personal data unless you have the
          necessary legal basis to do so</li>
      </ul>
    </div>

    <div className="legal-section" id="ip">
      <h2>8. Intellectual Property</h2>
      <p>
        The KeeDocs name, logo, software, and all associated intellectual property are owned by or
        licensed to us. Nothing in these Terms transfers any ownership rights to you. You may not
        copy, modify, distribute, or create derivative works of the Service without our prior
        written consent.
      </p>
    </div>

    <div className="legal-section" id="retention">
      <h2>9. Data Retention</h2>
      <ul>
        <li><strong>Anonymous uploads:</strong> deleted automatically after 7 days</li>
        <li><strong>Account documents:</strong> retained while your account is active; deleted
          within 30 days of account closure</li>
        <li><strong>Activity logs:</strong> retained for up to 90 days for security and abuse
          prevention purposes</li>
      </ul>
      <p>
        You may delete your documents at any time from the dashboard. Account closure requests
        can be submitted via the <Link to="/contact">Contact</Link> page.
      </p>
    </div>

    <div className="legal-section" id="liability">
      <h2>10. Disclaimer and Limitation of Liability</h2>
      <p>
        <strong>You use the Service entirely at your own risk.</strong> The Service is provided
        "as is" and "as available" without warranties of any kind, express or implied, including
        but not limited to warranties of merchantability, fitness for a particular purpose,
        non-infringement, or uninterrupted availability.
      </p>
      <p>
        <strong>KeeDocs does not accept any responsibility</strong> for loss, damage, or harm
        arising from your use of or inability to use the Service, including but not limited to:
      </p>
      <ul>
        <li>Loss, corruption, or unauthorised access to your documents or data</li>
        <li>Service interruptions, downtime, or data loss</li>
        <li>Errors or inaccuracies in the Service</li>
        <li>Actions taken by third parties who gain access to content you have shared</li>
        <li>Any reliance placed on information or content obtained through the Service</li>
      </ul>
      <p>
        To the fullest extent permitted by applicable law, KeeDocs shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages, regardless of the
        cause of action or the theory of liability, even if KeeDocs has been advised of the
        possibility of such damages.
      </p>
      <p>
        Our total aggregate liability for any claim arising out of or relating to the Service
        shall not exceed the greater of (a) the amount you paid us in the twelve months
        preceding the claim, or (b) USD $10.
      </p>
    </div>

    <div className="legal-section" id="termination">
      <h2>11. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any time, with or without notice,
        for conduct that we believe violates these Terms or is harmful to other users, us, or third
        parties. You may close your account at any time by contacting us.
      </p>
      <p>
        Upon termination, your documents will be deleted within 30 days. Provisions of these Terms
        that by their nature should survive termination shall survive, including but not limited to
        Sections 5, 7, and 9.
      </p>
    </div>

    <div className="legal-section" id="changes">
      <h2>12. Changes to These Terms</h2>
      <p>
        <strong>These Terms are subject to change at any time without prior notice.</strong> We
        will update the effective date at the top of this page when changes are made. It is your
        responsibility to review these Terms periodically. Continued use of the Service after
        any changes are posted constitutes your acceptance of the revised Terms.
      </p>
    </div>

    <div className="legal-section" id="contact-terms">
      <h2>13. Contact</h2>
      <div className="legal-contact">
        <p>
          If you have questions about these Terms, please reach out via our{' '}
          <Link to="/contact">Contact page</Link> or email us at{' '}
          <a href="mailto:legal@keedocs.com">legal@keedocs.com</a>.
        </p>
      </div>
    </div>
  </div>
);

export default TermsPage;
